import { createCodedError } from '~/common/errors/CodedError';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import {
  EsignatureActivities,
  EsignatureActivity,
} from '~/features/ticket/types/esignature';
import { JsonApiErrorResponse } from '~/redux/api/ApiTypes';
import { testIsAxiosError } from '~/redux/api/utils';
import { everest } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

type Request = {
  ticketId: Uuid;
};

type ServerResponse = {
  data: {
    attributes: {
      activities: Array<{
        message: string;
        timestamp: string;
        username: string | null;
      }>;
    };
  };
};

export const getEsignatureActivities = async (
  request: Request,
): Promise<Nullable<EsignatureActivities>> => {
  try {
    const serverResponse: ServerResponse = await everest.get(
      `tickets/${request.ticketId}/packages/activities`,
    );

    return mapResponse(serverResponse);
  } catch (error) {
    if (
      testIsAxiosError(error) &&
      error.response?.status === HttpStatusCodeType.NotFound
    ) {
      // if there are no activities for this ticket, server returns 404
      return null;
    }

    if (testIsAxiosError<JsonApiErrorResponse>(error) && error.response) {
      const errorResponse = error.response.data.errors[0];
      throw createCodedError({
        message: errorResponse.detail ?? errorResponse.title,
        statusCode: errorResponse.status,
        title: errorResponse.title,
      });
    }

    throw error;
  }
};

const mapResponse = (
  serverResponse: ServerResponse,
): Nullable<EsignatureActivities> => {
  if (serverResponse.data === null) {
    return null;
  }

  return {
    activities: serverResponse.data.attributes.activities.map(
      (activity): EsignatureActivity => ({
        message: activity.message,
        //l10n-ignore: API parsing no formatting required
        date: new Date(activity.timestamp),
        userName: activity.username,
      }),
    ),
  };
};
